<template>
  <div class="freebets-round pt-0 text-slate-900 animation-slide relative">
    <div class="flex flex-col gap-3">
      <Filters
        :options="options"
        :showActionButton="true"
        :search="handleFilterSearch"
        :resetFilter="resetFilter"
        :resetPagination="resetPagination"
      >
      </Filters>

      <div class="flex w-full items-center justify-end gap-3">
        <button
          @click="handleAddPlayer"
          class="group flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition-all hover:border-blue-300 hover:bg-blue-50 hover:text-blue-600 active:scale-95"
        >
          Add player
          <UserPlusIcon
            class="h-4 w-4 text-gray-400 transition-colors group-hover:text-blue-500"
          />
        </button>

        <button
          @click="handleImportPlayer"
          class="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-all hover:bg-blue-700 hover:shadow-md active:scale-95"
        >
          Import player
          <DocumentPlusIcon class="h-4 w-4 text-blue-100" />
        </button>
      </div>

      <div class="mx-auto py-10" v-if="isLoading">
        <Loader />
      </div>
      <div
        v-else-if="tableData.length === 0"
        class="flex-1 flex flex-col items-center justify-center py-10 text-gray-400"
      >
        <i class="pi pi-search text-4xl mb-3 opacity-80"></i>
        <span class="font-medium">No data found.</span>
        <small>Try adjusting your filters or search criteria.</small>
      </div>
      <DataTable
        v-else
        :value="tableData"
        :lazy="true"
        :paginator="true"
        :rows="selectedLimit"
        :totalRecords="totalElements"
        :loading="false"
        @page="onPage($event)"
        @sort="onSort($event)"
        sortMode="single"
        :sortField="sortField"
        :sortOrder="sortOrder"
        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
        :rowsPerPageOptions="[15, 25, 50, 100]"
        responsiveLayout="scroll"
        removableSort
        class="table-container-custom shadow-sm rounded-2xl overflow-hidden border border-1 mb-3"
      >
        <template #empty>
          <div
            class="flex flex-col items-center justify-center py-12 text-slate-400"
          >
            <i class="pi pi-search text-4xl mb-3 opacity-20"></i>
            <p class="text-lg font-medium">Nessun giocatore trovato</p>
            <p class="text-sm">Prova a cambiare i filtri di ricerca</p>
          </div>
        </template>

        <Column
          field="player_id"
          header="Player ID"
          :sortable="true"
          class="font-semibold text-slate-700"
        />

        <Column field="created_at" header="Created At" :sortable="true">
          <template #body="slotProps">
            <span class="text-slate-500">{{
              formatDate(slotProps.data.created_at)
            }}</span>
          </template>
        </Column>

        <Column field="updatedAt" header="Updated At" :sortable="true">
          <template #body="slotProps">
            <span class="text-slate-500">{{
              formatDate(slotProps.data.updatedAt)
            }}</span>
          </template>
        </Column>

        <Column
          header="Action"
          headerStyle="width: 6rem; text-align: center"
          bodyStyle="text-align: center"
        >
          <template #body="slotProps">
            <Button
              icon="pi pi-trash"
              class="p-button-rounded rounded-full p-button-text p-button-danger hover:bg-red-50 transition-colors"
              @click="removePlayerFromFreebet(slotProps.data)"
              v-tooltip.top="'Remove Player'"
            />
          </template>
        </Column>
      </DataTable>
    </div>
  </div>

  <ImportPlayerToFreebet
    v-if="showImportPlayer"
    :detail="detail"
    @import-player-modal-open="handleImportPlayer"
  />

  <AddPlayerToFreebet
    v-if="showAddPlayerModal"
    :detail="detail"
    @add-player-modal-open="handleAddPlayer"
  />
</template>

<script>
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Button from "primevue/button";
import Filters from "../table/Filters.vue";
import { getFetch, deleteRequest } from "@/api/axiosClient.js";
import Swal from "sweetalert2";
import { UserPlusIcon, DocumentPlusIcon } from "@heroicons/vue/24/outline";
import AddPlayerToFreebet from "../AddPlayerToFreebet.vue";
import ImportPlayerToFreebet from "../ImportPlayerToFreebet.vue";
import Loader from "../Loader.vue";

export default {
  name: "FreebetRestriction",
  props: {
    freebet_id: { type: String, required: true },
    skinFilt: { type: Number, required: true },
    detail: { type: Object, required: true },
  },
  components: {
    DataTable,
    Column,
    Button,
    Filters,
    UserPlusIcon,
    DocumentPlusIcon,
    AddPlayerToFreebet,
    ImportPlayerToFreebet,
    Loader,
  },
  data() {
    return {
      tableData: [],
      isLoading: false,
      totalElements: 0,
      currentPage: 1,
      selectedLimit: 15,
      sortField: "created_at",
      sortOrder: -1,
      showImportPlayer: false,
      showAddPlayerModal: false,
      player_id: "",
      options: [
        {
          label: "Player ID",
          type: "text",
          placeholder: "Search for Player ID",
          property: "player_id",
        },
        {
          label: "Freebet ID",
          type: "text",
          placeholder: "Locked",
          property: "freebet_id",
          disabled: true,
        },
      ],
    };
  },
  async mounted() {
    await this.search();
  },
  methods: {
    formatDate(date) {
      if (!date) return "-";
      return new Date(date).toLocaleString("it-IT", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    async onPage(event) {
      this.currentPage = event.page + 1;
      this.selectedLimit = event.rows;
      await this.search();
    },

    async onSort(event) {
      this.sortField = event.sortField;
      this.sortOrder = event.sortOrder;
      await this.search();
    },

    async handleFilterSearch(filterData) {
      this.currentPage = 1;
      await this.search();
    },

    async resetFilter() {
      this.player_id = "";
      this.currentPage = 1;
      this.sortField = "created_at";
      this.sortOrder = -1;
      await this.search();
    },

    async resetPagination() {
      this.currentPage = 1;
    },

    async handleAddPlayer() {
      this.showAddPlayerModal = !this.showAddPlayerModal;
    },

    async handleImportPlayer() {
      this.showImportPlayer = !this.showImportPlayer;
      document.body.style.overflow = this.showImportPlayer ? "hidden" : "";
    },

    async removePlayerFromFreebet(row) {
      const result = await Swal.fire({
        title: "Are you sure?",
        text: `Player ${row.player_id} will be removed from this campaign.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#ef4444",
        confirmButtonText: "Yes, remove it!",
        cancelButtonText: "Cancel",
      });

      if (result.isConfirmed) {
        try {
          this.isLoading = true;
          await deleteRequest(`freebets/restricted/${this.freebet_id}`, {
            player_id: row.player_id,
          });
          await this.search();
          Swal.fire("Deleted!", "The player has been removed.", "success");
        } catch (error) {
          Swal.fire("Error", "Could not remove the player.", "error");
        } finally {
          this.isLoading = false;
        }
      }
    },

    async search() {
      this.isLoading = true;
      try {
        const params = {
          skin_id: this.skinFilt,
          page: this.currentPage,
          limit: this.selectedLimit,
          player_id: this.player_id.trim() || undefined,
          sortField: this.sortField,
          sortOrder:
            this.sortOrder === 1
              ? "asc"
              : this.sortOrder === -1
              ? "desc"
              : undefined,
        };

        const response = await getFetch(
          `freebets/restricted/${this.freebet_id}`,
          params,
        );

        this.tableData = response.data;
        this.totalElements = response.totalItems;
      } catch (error) {
        console.error("Fetch error:", error);
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>
